import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// --- IMPORT YOUR LOCAL IMAGES ---
import ImageOne from '../HomePage/Images/image.png';
// import ImageTwo from '../HomePage/Images/Banner2.png';
// import ImageThree from '../HomePage/Images/Banner3.png';

const slides = [
  {
    id: 1,
    image: ImageOne,
    link: "https://wa.me/message/MDG2ZSZNG6P6G1",
    label: "Contact Us",
  },
  // Uncomment and add more slides when ready:
  // {
  //   id: 2,
  //   image: ImageTwo,
  //   link: "https://wa.me/message/MDG2ZSZNG6P6G1",
  //   label: "Contact Us",
  // },
];

const AUTOPLAY_INTERVAL = 5000;

const BannerScroller = () => {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  const goTo = useCallback((index) => {
    setCurrent(index);
  }, []);

  const goNext = useCallback(() => {
    setCurrent((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  }, []);

  const goPrev = useCallback(() => {
    setCurrent((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  }, []);

  useEffect(() => {
    if (slides.length <= 1 || paused) return;
    const timer = setInterval(goNext, AUTOPLAY_INTERVAL);
    return () => clearInterval(timer);
  }, [paused, goNext]);

  return (
    <section
      className="relative w-full overflow-hidden bg-zinc-950 select-none"
      style={{ aspectRatio: '16/6', minHeight: '220px', maxHeight: '90vh' }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setPaused(false)}
    >
      {/* ── Slide Track ── */}
      <motion.div
        className="flex h-full"
        style={{ width: `${slides.length * 100}%` }}
        animate={{ x: `-${(current * 100) / slides.length}%` }}
        transition={{ duration: 0.75, ease: [0.32, 0.72, 0, 1] }}
      >
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className="relative h-full"
            style={{ width: `${100 / slides.length}%` }}
          >
            <a
              href={slide.link}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full h-full group"
              aria-label={slide.label || `Slide ${index + 1}`}
            >
              {/* Image */}
              <img
                src={slide.image}
                alt={`Banner ${index + 1}`}
                className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-[8s] ease-out group-hover:scale-[1.03]"
                draggable={false}
              />

              {/* Subtle gradient vignette */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />

              {/* Hover CTA pill */}
              <div className="absolute bottom-12 sm:bottom-14 md:bottom-16 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0 pointer-events-none z-10">
                <span className="flex items-center gap-2 bg-white text-black text-xs sm:text-sm font-semibold px-4 py-2 rounded-full shadow-xl tracking-wide whitespace-nowrap">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  {slide.label || 'Open Form'}
                </span>
              </div>
            </a>
          </div>
        ))}
      </motion.div>

      {/* ── Prev / Next Arrows (only when >1 slide) ── */}
      {slides.length > 1 && (
        <>
          <button
            onClick={goPrev}
            aria-label="Previous slide"
            className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 z-20 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-black/40 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur-sm transition-all duration-200 hover:scale-110 active:scale-95"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            onClick={goNext}
            aria-label="Next slide"
            className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 z-20 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-black/40 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur-sm transition-all duration-200 hover:scale-110 active:scale-95"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* ── Pagination (dots + progress bars) ── */}
      {slides.length > 1 && (
        <div className="absolute bottom-4 sm:bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2 z-30">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goTo(index)}
              aria-label={`Go to slide ${index + 1}`}
              className="group relative flex items-center justify-center"
            >
              {/* Number label (shows on active) */}
              <span
                className={`absolute -top-5 text-[10px] font-bold tracking-widest transition-opacity duration-300 ${
                  current === index ? 'opacity-60 text-white' : 'opacity-0'
                }`}
              >
                {String(index + 1).padStart(2, '0')}
              </span>

              {/* Bar */}
              <div
                className={`relative rounded-full overflow-hidden transition-all duration-300 ${
                  current === index
                    ? 'w-10 sm:w-12 h-1.5 bg-white/30'
                    : 'w-2 h-2 bg-white/30 hover:bg-white/60 rounded-full'
                }`}
              >
                {current === index && (
                  <motion.div
                    key={`progress-${index}-${current}`}
                    initial={{ scaleX: 0 }}
                    animate={paused ? {} : { scaleX: 1 }}
                    transition={{ duration: AUTOPLAY_INTERVAL / 1000, ease: 'linear' }}
                    className="absolute inset-0 bg-white origin-left rounded-full"
                  />
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* ── Single-slide indicator (no pagination needed) ── */}
      {slides.length === 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30">
          <div className="w-8 h-1 rounded-full bg-white/60" />
        </div>
      )}
    </section>
  );
};

export default BannerScroller;